import React, {
    useState, useEffect, useCallback, useContext, useRef
} from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import { useValidation } from 'gw-portals-validation-react';
import { useSgHandleSubQuestions, SgSessionStorageService } from 'saga-modules';
import { TranslatorContext } from '@jutro/locale';
import { events } from 'gw-platform-events-js';
import { WizardContext } from 'gw-portals-wizard-react';
import CommonConstants from 'sg-common-constants';
import messages from '../SpmVehicleModifications.messages';
import metadata from './SpmVehicleModificationForm.metadata.json5';

function SpmVehicleModificationForm(props) {
    const {
        id,
        hideModificationForm,
        refreshDisplayedModifications,
        modelVM,
        updateData,
        modificationIndex,
        cancelOrRemoveModification,
        onValidate: onValidateParent,
        messageParams,
        isInitEditRender,
        setIsInitEditRender,
        showButtons,
        showModificationHeader,
        modificationHeaderMessage
    } = props;
    const [invalidSubQuestions, updateInvalidSubQuestions] = useState(
        SgSessionStorageService.getItem(SgSessionStorageService.KEY.INVALID_SUB_QUESTIONS)
    );
    const [localShowError, setLocalShowError] = useState(false);
    const [showModificationErrors, setShowModificationErrors] = useState(false);
    const currentModification = modelVM.lobData.privateMotor.coverables.spmVehicle.vehicleModifications.children[modificationIndex];
    const hasSavedModification = useRef(false);
    const {
        isComponentValid,
        onValidate
    } = useValidation(id);
    const translator = useContext(TranslatorContext);
    const wizardContext = useContext(WizardContext);

    useEffect(() => {
        if (onValidateParent) {
            onValidateParent(isComponentValid, id);
        }
    }, [id, isComponentValid, onValidateParent]);

    useEffect(() => {
        const updateInternalError = () => {
            setLocalShowError(true);
            setShowModificationErrors(true);
        };

        events.on(CommonConstants.SHOW_VALIDATION_ERROR_EVENT, updateInternalError);
        return () => {
            events.removeListener(
                CommonConstants.SHOW_VALIDATION_ERROR_EVENT,
                updateInternalError
            );
        };
    }, [setLocalShowError, setShowModificationErrors]);

    const updateModifications = useCallback(() => {
        updateData(modelVM);
    }, [modelVM, updateData]);

    useEffect(() => {
        if (isInitEditRender) {
            setIsInitEditRender(false);
            return;
        }
        _.set(currentModification, 'modificationType.value', undefined);
        const filteredSubQuestions = _.filter(invalidSubQuestions, (subQuestion) => {
            return subQuestion !== 'modificationType';
        });
        SgSessionStorageService.setItem(SgSessionStorageService.KEY.INVALID_SUB_QUESTIONS, filteredSubQuestions);
        setShowModificationErrors(false);
        updateModifications();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentModification.modificationCategory.value]);

    const saveModification = useCallback(() => {
        if (isComponentValid) {
            hasSavedModification.current = true;
            refreshDisplayedModifications();
            hideModificationForm();
            setShowModificationErrors(false);
        }
    }, [isComponentValid, hideModificationForm, refreshDisplayedModifications]);

    const cancelModification = useCallback(() => {
        cancelOrRemoveModification(modificationIndex);
        hideModificationForm();
    }, [cancelOrRemoveModification, modificationIndex, hideModificationForm]);

    useEffect(() => {
        const isModificationValid = currentModification.aspects.subtreeValid;
        return () => {
            setTimeout(() => {
                if (!hasSavedModification.current && !isModificationValid
                    && cancelOrRemoveModification && !window.location.hash.includes(wizardContext.currentStep.path)) { // only change on change of location
                    cancelOrRemoveModification(modificationIndex);
                    if (!modelVM.lobData.privateMotor.coverables.spmVehicle.vehicleModifications.length) {
                        modelVM.lobData.privateMotor.coverables.spmVehicle.hasModifications.value = undefined;
                        updateData(modelVM);
                    }
                }
            });
        };
    }, [currentModification]);

    useEffect(() => {
        return () => {
            if (onValidateParent) {
                // workaround when component was not valid but is no longer needed (hidden)
                onValidateParent(true, id);
            }
        };
    }, []);

    useSgHandleSubQuestions({
        updateModel: updateModifications,
        updateInvalidSubQuestions,
        modelVM: currentModification,
        paths: [
            'modificationCategory',
            'modificationType',
            'hasNormalFootControl',
            'disabledDriver',
            'isLowPerfIncrease',
            'isTowingPerformance',
            'hasBodyKit',
            'hasSleepingCookingFacilities',
            {
                path: 'modificationCost',
                emptyValue: {
                    amount: undefined
                }
            },
        ]
    });

    const resolvers = {
        resolveCallbackMap: {
            saveModification: saveModification,
            cancelModification: cancelModification
        }
    };

    const overrideProps = {
        modificationCategory: {
            showErrors: showModificationErrors && invalidSubQuestions.includes('modificationCategory')
        },
        saveModificationButton: {
            visible: showButtons
        },
        cancelModificationButton: {
            visible: showButtons
        },
        vehicleModificationFormValidationMessage: {
            visible: showButtons && localShowError && isComponentValid
        },
        modificationType: {
            disabled: _.isNil(currentModification.modificationCategory.value),
            visible: (!_.isNil(currentModification.modificationCategory.value)
                && currentModification.modificationCategory.value.code !== 'portal_motorhome')
                || _.isNil(currentModification.modificationCategory.value),
            showErrors: showModificationErrors && invalidSubQuestions.includes('modificationType')
        },
        howIsCarModified: {
            content: translator(messages.HowIsCarModifiedLabel, messageParams)
        },
        hasNormalFootControl: {
            visible: currentModification.hasNormalFootControl.aspects.required,
            label: translator(messages.NormalFootControlLabel, messageParams),
            showErrors: showModificationErrors && invalidSubQuestions.includes('hasNormalFootControl')
        },
        disabledDriver: {
            visible: currentModification.disabledDriver.aspects.required,
            showErrors: showModificationErrors && invalidSubQuestions.includes('disabledDriver')
        },
        isLowPerfIncrease: {
            visible: currentModification.isLowPerfIncrease.aspects.required,
            showErrors: showModificationErrors && invalidSubQuestions.includes('isLowPerfIncrease')
        },
        isTowingPerformance: {
            visible: currentModification.isTowingPerformance.aspects.required,
            label: translator(messages.TowingPerformanceLabel, messageParams),
            showErrors: showModificationErrors && invalidSubQuestions.includes('isTowingPerformance')
        },
        hasBodyKit: {
            visible: currentModification.hasBodyKit.aspects.required,
            showErrors: showModificationErrors && invalidSubQuestions.includes('hasBodyKit')
        },
        hasSleepingCookingFacilities: {
            visible: currentModification.hasSleepingCookingFacilities.aspects.required,
            showErrors: showModificationErrors && invalidSubQuestions.includes('hasSleepingCookingFacilities')
        },
        modificationCost: {
            visible: currentModification.modificationCost.aspects.required,
            showErrors: showModificationErrors && invalidSubQuestions.includes('modificationCost'),
        },
        modificationHeader: {
            visible: showModificationHeader,
            content: translator(modificationHeaderMessage, { modificationNumber: (modificationIndex + 1).toString() }),
        }

    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={currentModification}
            overrideProps={overrideProps}
            onModelChange={updateModifications}
            showErrors={showModificationErrors}
            onValidationChange={onValidate}
            callbackMap={resolvers.resolveCallbackMap}
        />
    );
}

SpmVehicleModificationForm.propTypes = {
    id: PropTypes.string,
    modelVM: PropTypes.shape({}).isRequired,
    updateData: PropTypes.func.isRequired,
    modificationIndex: PropTypes.number.isRequired,
    showButtons: PropTypes.bool,
    refreshDisplayedModifications: PropTypes.func,
    hideModificationForm: PropTypes.func
};

SpmVehicleModificationForm.defaultProps = {
    id: undefined,
    showButtons: true,
    refreshDisplayedModifications: () => {},
    hideModificationForm: () => {}
};

export default SpmVehicleModificationForm;
